"use client";
import { useState, useMemo } from "react";
import axios from "axios";

const API_BASE = "https://erp-xe9u.onrender.com/api";

// ─────────────────────────────────────────────────────────────────────────────
// BatchRateChangeModal
//
// Props:
//   isOpen       boolean
//   onClose      () => void
//   onSuccess    (updatedWO: { itemCount, estimatedTotal } | null) => void
//                  — called after a successful submit; updatedWO contains the
//                    server-recalculated WO totals so the parent can patch state.
//   woId         string
//   items        Array<itemRow>   — selected rows (ungrouped) or group sub-items
//   mode         "selection" | "group"
// ─────────────────────────────────────────────────────────────────────────────
export default function BatchRateChangeModal({
  isOpen,
  onClose,
  onSuccess,
  woId,
  items = [],
  mode = "selection",
}) {
  const [newRate, setNewRate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Derive representative "current rate" for display ────────────────────
  const rateInfo = useMemo(() => {
    const rates = [...new Set(items.map((i) => Number(i.unitRate || 0)))];
    if (rates.length === 0) return { mixed: false, rate: 0 };
    if (rates.length === 1) return { mixed: false, rate: rates[0] };
    return {
      mixed: true,
      rates,
      min: Math.min(...rates),
      max: Math.max(...rates),
    };
  }, [items]);

  const fmt = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}`;

  const fmtQty = (n) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const parsedRate = parseFloat(newRate);
  const rateValid = !isNaN(parsedRate) && parsedRate > 0;

  // ── Client-side preview (server does the authoritative calc) ─────────────
  const previewRows = useMemo(() => {
    if (!rateValid) return [];
    return items.map((item) => {
      const oldRate = Number(item.unitRate || 0);
      const netQty = item.netRequiredQty ?? item.quantity ?? 0;
      const charged = item.qtyOrdered ?? item.qtyCharged ?? 0;
      const pending = item.qtyPending ?? 0;
      const closeoutQty = Math.max(0, netQty - charged - pending);
      const rateChanged = Math.abs(parsedRate - oldRate) > 0.0001;
      const netCostImpact = rateChanged
        ? closeoutQty * parsedRate - closeoutQty * oldRate
        : 0;
      return {
        lineItemId: item.lineItemId || item._id,
        assemblyId: item.assemblyId,
        description:
          item.description ||
          item.costCodeDescription ||
          item.itemDescription ||
          "–",
        conditionName: item.conditionName || "",
        bidArea: item.bidArea || "",
        uom: item.uom || "",
        oldRate,
        netQty,
        charged,
        pending,
        closeoutQty,
        netCostImpact,
        rateChanged,
        willSkip: !rateChanged,
      };
    });
  }, [items, parsedRate, rateValid]);

  const totalImpact = previewRows.reduce(
    (s, r) => s + (r.netCostImpact || 0),
    0,
  );
  const skippedCount = previewRows.filter((r) => r.willSkip).length;
  const activeCount = previewRows.filter((r) => !r.willSkip).length;

  const handleSubmit = async () => {
    if (!rateValid) return;
    setError("");
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        items: items.map((i) => ({
          assemblyId: String(i.assemblyId),
          lineItemId: String(i.lineItemId || i._id),
        })),
        newRate: parsedRate,
        reason: reason.trim() || undefined,
      };
      const res = await axios.post(
        `${API_BASE}/workorders2/${woId}/items/batch-rate-change`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Pass the server-returned WO totals back to the parent
      onSuccess(res.data?.updatedWO || null);
      handleClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to apply rate change",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewRate("");
    setReason("");
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-80"
          onClick={handleClose}
        />

        {/* Panel */}
        <div className="relative inline-block w-full max-w-3xl px-6 pt-6 pb-6 text-left align-middle transition-all transform bg-gray-800 rounded-xl shadow-2xl border border-gray-600">
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-xl font-bold text-white">
                Batch Rate Change
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                {mode === "group"
                  ? `Apply a new rate to all ${items.length} items in this group`
                  : `Apply a new rate to ${items.length} selected item${items.length !== 1 ? "s" : ""}`}{" "}
                <span className="text-gray-500">
                  — quantities adjust per-item based on charged/pending
                </span>
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white text-2xl leading-none ml-4 shrink-0"
            >
              ×
            </button>
          </div>

          {/* Current rate pill */}
          <div className="mb-5 p-3 bg-gray-900 rounded-lg text-sm">
            <span className="text-gray-400">Current rate: </span>
            {rateInfo.mixed ? (
              <span className="text-yellow-400 font-medium">
                Mixed ({fmt(rateInfo.min)} – {fmt(rateInfo.max)})
              </span>
            ) : (
              <span className="text-white font-medium">
                {fmt(rateInfo.rate)}
              </span>
            )}
          </div>

          {/* Inputs */}
          <div className="mb-4 flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                New Unit Rate *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={newRate}
                  onChange={(e) => {
                    setNewRate(e.target.value);
                    setError("");
                  }}
                  placeholder="0.0000"
                  className="w-full pl-7 pr-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Reason <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Revised unit pricing"
                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Preview table */}
          {rateValid && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-300">
                  Preview
                  {skippedCount > 0 && (
                    <span className="ml-2 text-xs font-normal text-yellow-500">
                      ({skippedCount} item{skippedCount !== 1 ? "s" : ""}{" "}
                      already at this rate — will be skipped)
                    </span>
                  )}
                </h4>
                <span
                  className={`text-sm font-bold ${
                    totalImpact > 0
                      ? "text-green-400"
                      : totalImpact < 0
                        ? "text-red-400"
                        : "text-gray-400"
                  }`}
                >
                  Net budget impact: {totalImpact >= 0 ? "+" : ""}
                  {fmt(totalImpact)}
                </span>
              </div>

              <div className="rounded-lg border border-gray-700 overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-400 font-medium uppercase">
                        Description
                      </th>
                      <th className="px-3 py-2 text-right text-gray-400 font-medium uppercase">
                        Net Req.
                      </th>
                      <th className="px-3 py-2 text-right text-green-400 font-medium uppercase">
                        Charged
                      </th>
                      <th className="px-3 py-2 text-right text-yellow-400 font-medium uppercase">
                        Pending
                      </th>
                      <th className="px-3 py-2 text-right text-orange-400 font-medium uppercase">
                        Open Qty
                      </th>
                      <th className="px-3 py-2 text-right text-gray-400 font-medium uppercase">
                        Old Rate
                      </th>
                      <th className="px-3 py-2 text-right text-blue-400 font-medium uppercase">
                        New Rate
                      </th>
                      <th className="px-3 py-2 text-right text-gray-300 font-medium uppercase">
                        Δ Budget
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/60">
                    {previewRows.map((row, i) => (
                      <tr
                        key={String(row.lineItemId) + i}
                        className={`${
                          row.willSkip
                            ? "opacity-40 bg-gray-800/50"
                            : i % 2 === 0
                              ? "bg-gray-800/30"
                              : ""
                        }`}
                      >
                        <td
                          className="px-3 py-2 text-gray-200 max-w-[180px] truncate"
                          title={row.description}
                        >
                          {row.willSkip && (
                            <span className="mr-1.5 text-yellow-600 text-[10px] uppercase font-semibold">
                              skip
                            </span>
                          )}
                          {row.description}
                          {row.conditionName && (
                            <span className="ml-1 text-gray-500 text-[10px]">
                              ({row.conditionName})
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300">
                          {fmtQty(row.netQty)}
                        </td>
                        <td className="px-3 py-2 text-right text-green-400">
                          {row.charged > 0 ? fmtQty(row.charged) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right text-yellow-400">
                          {row.pending > 0 ? fmtQty(row.pending) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-orange-300">
                          {fmtQty(row.closeoutQty)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-400">
                          {fmt(row.oldRate)}
                        </td>
                        <td className="px-3 py-2 text-right text-blue-300 font-medium">
                          {fmt(parsedRate)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-semibold ${
                            row.willSkip
                              ? "text-gray-500"
                              : row.netCostImpact > 0
                                ? "text-green-400"
                                : row.netCostImpact < 0
                                  ? "text-red-400"
                                  : "text-gray-500"
                          }`}
                        >
                          {row.willSkip
                            ? "—"
                            : `${row.netCostImpact >= 0 ? "+" : ""}${fmt(row.netCostImpact)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Explanation callout */}
              <div className="mt-2 px-3 py-2 bg-blue-900/20 border border-blue-700/30 rounded-lg text-xs text-blue-300">
                <strong>How this works:</strong> Only the{" "}
                <span className="text-orange-300">
                  open (un-charged) quantity
                </span>{" "}
                moves to the new rate. Already-charged and pending quantities
                stay at their original rate. A{" "}
                <code>price_change_closeout</code> and{" "}
                <code>price_change_new</code> variance pair is written per item.
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="text-sm text-gray-500">
              {activeCount > 0 && rateValid && (
                <>
                  {activeCount} item{activeCount !== 1 ? "s" : ""} will be
                  updated
                  {skippedCount > 0 && `, ${skippedCount} skipped`}
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-lg hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!rateValid || loading || activeCount === 0}
                className={`px-6 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  !rateValid || loading || activeCount === 0
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 text-white shadow-lg"
                }`}
              >
                {loading
                  ? "Applying…"
                  : activeCount === 0
                    ? "No changes to apply"
                    : `Apply Rate Change to ${activeCount} Item${activeCount !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
